import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import type { SxProps, Theme } from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTextDomainContext } from "@/app/providers";
import { isEqual } from "lodash";
import { string, func, array, bool } from "prop-types";
import type { DropdownOption } from "@/types";

type SimpleDropdownProps = {
  componentLabel?: string;
  onChange?: (event: DropdownOption | null) => void;
  options?: DropdownOption[];
  defaultValue?: string;
  size?: "small" | "medium";
  required?: boolean;
  showAll?: boolean;
  sx?: SxProps<Theme>;
  disableCache?: boolean;
};

export const SimpleDropdown = (props: SimpleDropdownProps) => {
  const { onChange } = props;
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  let cacheKey = "SimpleDropdown";
  if (props.componentLabel) {
    cacheKey = `SimpleDropdown-${props.componentLabel}`;
  }

  // Use useMemo for options to avoid dependency warnings
  const options = useMemo(() => {
    return (
      props.options || [
        {
          value: "",
          label: gettext("Select"),
        },
      ]
    );
  }, [props.options, gettext]);

  const selectSize = props.size || "medium";

  const [selectedValue, setSelectedValue] = useState(
    // localFilters ? localFilters.buildingMeterValue : gettext("all")
    "",
  );

  const handleChangeSelectedValue = (value: string) => {
    if (!props.disableCache && cacheKey && value) {
      localStorage.setItem(cacheKey, JSON.stringify(value));
    }
    setSelectedValue(value);
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedObject = options.find(
      (option) => option.value === event.target.value,
    );
    // setSelectedValue(event.target.value);
    handleChangeSelectedValue(event.target.value);
    // handleChangeSelectedValue(event.value);
    // setSelectedLabel(event.label);
    onChange(selectedObject);

    // (event) => {
    //   props.onChange(options.find(v => v.value === event.target.value));
    // }
  };

  useEffect(() => {
    if (selectedValue) {
      const defaultIndex = options.findIndex(
        (option) => option.value === selectedValue,
      );
      if (defaultIndex > -1) {
        const selected = options[defaultIndex];
        if (typeof onChange === "function") {
          onChange(selected);
        }
      }
    }
  }, [onChange, options, selectedValue]);

  useEffect(() => {
    const cacheValue = props.disableCache
      ? null
      : JSON.parse(localStorage.getItem(cacheKey) || "null");
    if (props.defaultValue) {
      const defaultIndex = options.findIndex(
        (x) => x.value === props.defaultValue,
      );
      if (defaultIndex > -1) {
        const selected = options[defaultIndex];

        if (cacheValue) {
          let selectedObject: DropdownOption | null = null;
          if (isEqual(options, props.options)) {
            selectedObject =
              options.find((option) => option.value === cacheValue) || null;
          }

          if (!selectedObject && options?.length) {
            selectedObject = options[0];
          }

          if (selectedObject?.value) {
            // Use setTimeout to defer state update and avoid cascading renders
            setTimeout(() => {
              setSelectedValue(String(selectedObject.value));
            }, 0);
          }
        } else {
          setTimeout(() => {
            setSelectedValue(String(selected.value));
          }, 0);
        }

        if (typeof onChange === "function") {
          onChange(selected);
        }
      }
    }
  }, [
    cacheKey,
    onChange,
    options,
    props.defaultValue,
    props.disableCache,
    props.options,
  ]);

  return (
    <FormControl fullWidth>
      <InputLabel id="select-label">{props.componentLabel}</InputLabel>
      <Select
        labelId="select-label"
        id="select"
        value={selectedValue}
        fullWidth
        onChange={handleChange}
        variant="outlined"
        size={selectSize}
      >
        {options.map((option: DropdownOption) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

SimpleDropdown.propTypes = {
  componentLabel: string,
  onChange: func,
  options: array,
  defaultValue: string,
  disableCache: bool,
  size: string,
};
